import React, { useId, useState } from 'react';
import { Brain, Database, DeviceMobile, Graph, ShieldCheck, TerminalWindow } from '@phosphor-icons/react';
import '../styles/visual-stories.css';

const stories = {
  delivery: {
    caption: 'From the first question to a working system.',
    steps: [
      { name: 'Understand', title: 'Start with the real problem.', detail: 'Map the work, speak with the people doing it, and agree on a useful first outcome.', inputs: ['People', 'Processes', 'Systems'], center: 'A clear brief', output: 'A practical plan' },
      { name: 'Build', title: 'Make the plan tangible.', detail: 'Design, engineer, and review working software alongside your team in two-week sprints.', inputs: ['Design', 'Engineering', 'Review'], center: 'Working software', output: 'A tested release' },
      { name: 'Operate', title: 'Make it yours to run.', detail: 'Connect monitoring, document the decisions, and give your team the tools to own the system.', inputs: ['Monitoring', 'Documentation', 'Feedback'], center: 'Your team', output: 'The next iteration' },
    ],
  },
  architecture: {
    caption: 'A connected system, from source to screen.',
    steps: [
      { name: 'Data', title: 'A foundation you can trust.', detail: 'Bring source systems together through clear contracts, validation, and governed access.', inputs: ['Source systems', 'Data contracts', 'Quality checks'], center: 'Trusted data', output: 'Useful information' },
      { name: 'Intelligence', title: 'Reasoning with boundaries.', detail: 'Connect models to the context and tools they need, with evaluation and human review.', inputs: ['Project context', 'Models', 'Tools'], center: 'Bounded agents', output: 'Reviewed actions' },
      { name: 'Product', title: 'Put the system to work.', detail: 'Deliver interfaces and APIs that fit the way your team works, with visibility into what happens.', inputs: ['Interfaces', 'APIs', 'Observability'], center: 'Your product', output: 'Everyday work' },
    ],
  },
  learning: {
    caption: 'Three parts. One useful working system.',
    steps: [
      { name: 'Brain', title: 'The model reasons.', detail: 'A model can interpret, draft, and plan. It needs the right context and checks to work well.', inputs: ['A question', 'Context', 'Instructions'], center: 'The model', output: 'A proposed answer' },
      { name: 'Memory', title: 'The project remembers.', detail: 'Documents and repositories preserve decisions, standards, and business context between sessions.', inputs: ['Decisions', 'Standards', 'Project files'], center: 'Shared memory', output: 'Relevant context' },
      { name: 'Hands', title: 'Tools turn intent into action.', detail: 'Connectors give an agent specific capabilities. Permissions and human review set its boundaries.', inputs: ['Permissions', 'Connectors', 'Human review'], center: 'Tool execution', output: 'A verified result' },
    ],
  },
  pharma: {
    caption: 'Commercial signals, connected to decisions.',
    steps: [
      { name: 'Connect', title: 'See the whole commercial picture.', detail: 'Connect distribution, patient, media, and field signals through shared identifiers and data contracts.', inputs: ['Distribution', 'Patient signals', 'Media & field'], center: 'Shared contracts', output: 'Connected sources' },
      { name: 'Govern', title: 'Resolve the gaps in the middle.', detail: 'Master customer identities, validate incoming feeds, and make ownership and freshness visible.', inputs: ['Customer master', 'Validation', 'Ownership'], center: 'Governed data', output: 'Trusted reporting' },
      { name: 'Activate', title: 'Get information to the work.', detail: 'Deliver governed reporting and integrations for commercial operations, finance, and field teams.', inputs: ['Reporting', 'CRM', 'Monitoring'], center: 'Commercial teams', output: 'Informed decisions' },
    ],
  },
};

export const SystemStory = ({ kind = 'delivery', compact = false }) => {
  const [selected, setSelected] = useState(0);
  const id = useId();
  const story = stories[kind];
  const step = story.steps[selected];
  return (
    <figure className={`system-story${compact ? ' system-story--compact' : ''}`}>
      <div className="system-story__controls" role="group" aria-label={`${kind} diagram stages`}>
        {story.steps.map((item, i) => (
          <button type="button" key={item.name} aria-pressed={selected === i} aria-controls={`${id}-detail`} onClick={() => setSelected(i)}>
            <span className="system-story__index" aria-hidden="true">0{i + 1}</span>{item.name}
          </button>
        ))}
      </div>
      <svg className="system-story__drawing" viewBox="0 0 600 280" role="img" aria-label={`${step.inputs.join(', ')} connect through ${step.center} to ${step.output}`}>
        <g className="system-story__grid" aria-hidden="true">
          {[40, 100, 160, 220, 280, 340, 400, 460, 520, 580].map(x => <path key={x} d={`M${x} 20V260`} />)}
          {[40, 100, 160, 220].map(y => <path key={y} d={`M20 ${y}H580`} />)}
        </g>
        <g className="system-story__wires" fill="none"><path d="M175 60H210V140H244M175 140H244M175 220H210V140M376 140H414" /></g>
        {step.inputs.map((label, i) => (
          <g key={i}><rect className="system-story__node" x="24" y={36 + i * 80} width="151" height="48" /><text x="99.5" y={65 + i * 80} textAnchor="middle">{label}</text></g>
        ))}
        <rect className="system-story__core" x="244" y="84" width="132" height="112" />
        <path className="system-story__seal" d="M298 106h24v24h-24z" />
        <text className="system-story__core-label" x="310" y="159" textAnchor="middle">{step.center}</text>
        <rect className="system-story__node" x="414" y="116" width="162" height="48" />
        <text x="495" y="145" textAnchor="middle">{step.output}</text>
        <path className="system-story__arrow" d="m405 134 9 6-9 6" fill="none" />
        <path className="system-story__feedback" d="M495 164v80H310v-48" fill="none" />
      </svg>
      <div className="system-story__mobile" role="img" aria-label={`${step.inputs.join(', ')} connect through ${step.center} to ${step.output}`}>
        <div className="system-story__mobile-inputs">{step.inputs.map(label => <span key={label}>{label}</span>)}</div>
        <span className="system-story__mobile-arrow">↓</span>
        <div className="system-story__mobile-core"><span className="seal seal--sm" />{step.center}</div>
        <span className="system-story__mobile-arrow">↓</span>
        <div className="system-story__mobile-output">{step.output}</div>
      </div>
      <div id={`${id}-detail`} className="system-story__detail" aria-live="polite" aria-atomic="true">
        <strong>{step.title}</strong><p>{step.detail}</p>
      </div>
      <figcaption>{story.caption}</figcaption>
    </figure>
  );
};

export const StatementBand = ({ children, caption = 'The AURABLOX approach' }) => (
  <figure className="statement-band">
    <div className="statement-band__inner">
      <span className="statement-band__mark" aria-hidden="true">“</span>
      <blockquote>{children}</blockquote>
      <figcaption>{caption}</figcaption>
    </div>
    <svg className="statement-band__art" viewBox="0 0 360 360" aria-hidden="true">
      <path d="M20 340V20H340V340ZM80 340V80H280V340M140 340V140H220V340" fill="none" stroke="currentColor" />
      <path d="M0 300 300 0M60 360 360 60" fill="none" stroke="var(--red)" strokeWidth="12" />
    </svg>
  </figure>
);

const marks = [DeviceMobile, Database, Brain, TerminalWindow, Graph, ShieldCheck];
export const CapabilityMark = ({ index = 0 }) => {
  const Icon = marks[index % marks.length];
  return <span className="capability-mark" aria-hidden="true"><Icon size={32} weight="light" /></span>;
};

const maturity = [
  { name: 'Manual', title: 'Work lives in individual hands.', detail: 'Spreadsheets and personal knowledge carry the process. Start by documenting the work and its handoffs.', nodes: 1 },
  { name: 'Experimenting', title: 'Useful tools, separate conversations.', detail: 'People use AI individually. The next step is shared context, clear standards, and agreed ways to review the work.', nodes: 2 },
  { name: 'Connected', title: 'Models, memory, and tools work together.', detail: 'Project context and connected tools support repeatable work. Teams review changes and verify results.', nodes: 3 },
  { name: 'Coordinated', title: 'Delegated work with human oversight.', detail: 'Agents coordinate bounded tasks across systems. People set priorities, approve consequential actions, and monitor outcomes.', nodes: 4 },
];

export const MaturityExplorer = () => {
  const [selected, setSelected] = useState(0);
  const item = maturity[selected];
  const id = useId();
  return (
    <div className="maturity-explorer">
      <div className="maturity-explorer__steps" role="group" aria-label="Explore the AI maturity stages">
        {maturity.map((stage, i) => <button type="button" key={stage.name} aria-pressed={selected === i} aria-controls={`${id}-panel`} onClick={() => setSelected(i)}><span>0{i + 1}</span>{stage.name}<span aria-hidden="true">↗</span></button>)}
      </div>
      <div className="maturity-explorer__panel" id={`${id}-panel`}>
        <svg viewBox="0 0 500 230" role="img" aria-label={`${item.name}: stage ${selected + 1} of a conceptual maturity framework, not a measured performance chart`}>
          <path d="M36 196H464" className="maturity-explorer__axis" />
          {[0, 1, 2, 3].map(i => <g key={i} className={i <= selected ? 'maturity-explorer__stage is-active' : 'maturity-explorer__stage'}><rect x={52 + i * 110} y={151 - i * 34} width="66" height={45 + i * 34} /><text x={85 + i * 110} y="220" textAnchor="middle">0{i + 1}</text></g>)}
          <path className="maturity-explorer__path" d="M85 138H195V104H305V70H415V36" />
        </svg>
        <div aria-live="polite" aria-atomic="true"><h3>{item.title}</h3><p>{item.detail}</p></div>
        <p className="maturity-explorer__caption">A progression framework, rather than a performance forecast.</p>
      </div>
    </div>
  );
};
